import { isTruthy } from '@ardoq/common-helpers';
import {
  ComponentGraphGroup,
  ComponentGraphNode,
  getGraphNodeOrGroupLabelForWorkspaceMode,
  GraphInterface,
  GraphItemsModel,
  ItemLabels,
  LabelsInterface,
  RepresentationInterface,
  SVGColorAttributes,
  TraversalAndGroupingResult,
  URLFieldValue,
} from '@ardoq/graph';
import { APIComponentAttributes } from '@ardoq/api-types';
import { toIdSet } from '@ardoq/core';
import {
  ConditionalFormattingState,
  RepresentationData,
} from '@ardoq/data-model';
import { GetRepresentationData } from 'tabview/relationshipsDiagramsViewModel/types';
import { WorkspaceRelationshipsViewSourceData } from 'tabview/relationsD3View/hierarchical/types';
import {
  addUpdated,
  getComponentRepresentationDataEntries,
  getComponentUrlFieldValueEntries,
  getGraphItemColorEntries,
  getGraphItemConditionalFormattingStateEntries,
  getItems,
  getUpdatedItems,
  setGraphItemClassProperties,
} from './graphItemUpdateUtils';

const getComponentItemLabelsEntry = (
  itemId: string,
  items: GraphItemsModel<ComponentGraphNode | ComponentGraphGroup>,
  rawGraph: TraversalAndGroupingResult
): [string, ItemLabels] | null => {
  const itemsResult = getItems(itemId, items, rawGraph);
  if (!itemsResult) return null;

  const { graphItem, rawGraphItem } = itemsResult;
  if (!rawGraphItem) return null;

  return [graphItem.id, getGraphNodeOrGroupLabelForWorkspaceMode(rawGraphItem)];
};

type GetUpdatedGraphItemDataArgs = {
  updatedNodeIds: Set<string>;
  updatedGroupIds: Set<string>;
  nodes: GraphItemsModel<ComponentGraphNode>;
  groups: GraphItemsModel<ComponentGraphGroup>;
  rawGraph: TraversalAndGroupingResult;
  viewInterface: LabelsInterface & GraphInterface & RepresentationInterface;
  getRepresentationData: GetRepresentationData;
};
const getUpdatedGraphItemDataEntries = ({
  updatedNodeIds,
  updatedGroupIds,
  nodes,
  groups,
  rawGraph,
  viewInterface,
  getRepresentationData,
}: GetUpdatedGraphItemDataArgs) => {
  const nodeLabelsData = [...updatedNodeIds]
    .map(itemId => getComponentItemLabelsEntry(itemId, nodes, rawGraph))
    .filter(isTruthy);

  const groupLabelsData = [...updatedGroupIds]
    .map(itemId => getComponentItemLabelsEntry(itemId, groups, rawGraph))
    .filter(isTruthy);

  const urlFieldValuesData = getComponentUrlFieldValueEntries(
    viewInterface,
    updatedNodeIds,
    updatedGroupIds,
    nodes,
    groups
  );

  const representationDataByComponentId = getComponentRepresentationDataEntries(
    updatedNodeIds,
    updatedGroupIds,
    nodes,
    groups,
    getRepresentationData
  );

  const nodeColorsByGraphItemId = getGraphItemColorEntries(
    viewInterface,
    updatedNodeIds,
    updatedGroupIds,
    nodes,
    groups
  );

  const conditionalFormattingState =
    getGraphItemConditionalFormattingStateEntries(
      viewInterface,
      updatedNodeIds,
      updatedGroupIds,
      nodes,
      groups
    );

  return {
    labelsData: [...nodeLabelsData, ...groupLabelsData],
    urlFieldValuesByComponentId: urlFieldValuesData,
    representationDataByComponentId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
  };
};

export const handleWorkspaceModeComponentsUpdate = (
  state: WorkspaceRelationshipsViewSourceData,
  components: APIComponentAttributes[]
): WorkspaceRelationshipsViewSourceData => {
  const {
    viewInterface,
    rawGraph,
    getRepresentationData,
    nodeLabelsData: previousNodeLabelsData,
    nodes,
    groups,
    urlFieldValuesByComponentId: previousUrlFieldValuesByComponentId,
    representationDataByComponentId: previousRepresentationDataByComponentId,
    nodeColorsByGraphItemId: previousColorsByGraphItemId,
    conditionalFormattingState: previousConditionalFormattingState,
  } = state;

  const updatedModelIds = toIdSet(components);

  const updatedGroupIds = getUpdatedItems(groups, updatedModelIds);
  const updatedNodeIds = getUpdatedItems(nodes, updatedModelIds);

  const newGraphItemData = getUpdatedGraphItemDataEntries({
    updatedNodeIds,
    updatedGroupIds,
    nodes,
    groups,
    rawGraph,
    viewInterface,
    getRepresentationData,
  });

  // Merge old data with fresh data for updated items
  const nodeLabelsData = new Map<string, ItemLabels>([
    ...previousNodeLabelsData,
    ...newGraphItemData.labelsData,
  ]);
  const urlFieldValuesByComponentId = new Map<string, URLFieldValue[] | null>([
    ...previousUrlFieldValuesByComponentId,
    ...newGraphItemData.urlFieldValuesByComponentId,
  ]);

  const representationDataByComponentId = new Map<
    string,
    RepresentationData | null
  >([
    ...previousRepresentationDataByComponentId,
    ...newGraphItemData.representationDataByComponentId,
  ]);

  const nodeColorsByGraphItemId = new Map<string, SVGColorAttributes>([
    ...previousColorsByGraphItemId,
    ...newGraphItemData.nodeColorsByGraphItemId,
  ]);

  const conditionalFormattingState = new Map<
    string,
    ConditionalFormattingState
  >([
    ...previousConditionalFormattingState,
    ...newGraphItemData.conditionalFormattingState,
  ]);

  setGraphItemClassProperties({
    itemIds: [...updatedGroupIds, ...updatedNodeIds],
    nodes,
    groups,
    nodeLabelsData,
    urlFieldValuesByComponentId,
    representationDataByComponentId,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
  });

  return {
    ...state,
    nodes: addUpdated(nodes, updatedNodeIds),
    groups: addUpdated(groups, updatedGroupIds),
    expandedStateChangingGroupId: null,
    urlFieldValuesByComponentId,
    representationDataByComponentId,
    nodeLabelsData,
    nodeColorsByGraphItemId,
    conditionalFormattingState,
  };
};
